/**
 * AdminFunnels — internal funnel dashboard.
 *
 * Mirrors the PostHog data NDG fires for every wedge product. PostHog is
 * still the canonical source of truth, but this lets the founder see a
 * snapshot inside the app — useful before PostHog is fully wired and as a
 * sanity-check forever after.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, Activity, TrendingDown, ChevronRight, Sparkles, Users,
  RefreshCw,
} from "lucide-react";
import { Button } from "../components/ui/button";

const API = process.env.REACT_APP_BACKEND_URL;

const RANGE_OPTIONS = [
  { days: 1, label: "24h" },
  { days: 7, label: "7d" },
  { days: 30, label: "30d" },
  { days: 90, label: "90d" },
];

export default function AdminFunnels() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [insights, setInsights] = useState(null);
  const [sections, setSections] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  const load = async (n = days) => {
    setLoading(true);
    try {
      const [s, i, h] = await Promise.all([
        axios.get(`${API}/api/analytics/funnels/summary?days=${n}`, { withCredentials: true }),
        axios.get(`${API}/api/analytics/funnels/insights?days=${n}`, { withCredentials: true }),
        axios.get(`${API}/api/analytics/homepage-sections?days=${n}`, { withCredentials: true }),
      ]);
      setData(s.data);
      setInsights(i.data);
      setSections(h.data);
    } catch { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { load(days); /* eslint-disable-line */ }, [days]);

  return (
    <div className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-slate-950 pb-24" data-testid="admin-funnels-page">
      <header className="sticky top-0 inset-x-0 z-30 backdrop-blur-xl bg-white/85 dark:bg-slate-950/85 border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-5xl mx-auto px-4 h-14 flex items-center gap-3">
          <button data-testid="af-back" onClick={() => nav("/admin")} className="p-2 -ml-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800">
            <ArrowLeft className="w-4 h-4 text-slate-500" />
          </button>
          <div className="flex-1">
            <p className="text-sm font-bold text-slate-900 dark:text-white">Conversion Funnels</p>
            <p className="text-[10.5px] text-slate-500 uppercase tracking-[0.18em]">Internal mirror · PostHog is canonical</p>
          </div>
          <button data-testid="af-refresh" onClick={() => load(days)} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800">
            <RefreshCw className={`w-4 h-4 text-slate-500 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-6 space-y-5">
        {/* Range picker */}
        <div data-testid="af-range" className="flex gap-2">
          {RANGE_OPTIONS.map((r) => (
            <button
              key={r.days}
              data-testid={`af-range-${r.days}`}
              onClick={() => setDays(r.days)}
              className={`text-[12px] font-bold rounded-full px-3 py-1.5 transition ${
                days === r.days
                  ? "bg-slate-900 dark:bg-white text-white dark:text-slate-900"
                  : "bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:border-slate-400"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>

        {/* INSIGHTS — auto-recommendation panel */}
        {insights && (
          <div data-testid="af-insights" className="rounded-2xl border border-amber-300 dark:border-amber-700 bg-gradient-to-br from-amber-50 to-amber-100/50 dark:from-amber-900/20 dark:to-amber-800/10 p-5">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-200 dark:bg-amber-700/30 border border-amber-300 dark:border-amber-700 flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-5 h-5 text-amber-700 dark:text-amber-300" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-amber-700 dark:text-amber-300 mb-1">
                  This {days === 1 ? "day" : `${days} days`} · what to fix next
                </div>
                <p data-testid="af-recommendation" className="text-base font-bold text-slate-900 dark:text-white leading-snug">
                  {insights.recommendation}
                </p>
                {insights.biggest_drop && (
                  <p data-testid="af-biggest-drop" className="text-[12.5px] text-slate-600 dark:text-slate-300 mt-2">
                    <span className="inline-flex items-center gap-1.5">
                      <TrendingDown className="w-3.5 h-3.5 text-rose-500" />
                      <strong>Biggest drop-off:</strong>
                    </span>{" "}
                    {insights.biggest_drop.from_stage.replace(/_/g, " ")} →{" "}
                    {insights.biggest_drop.to_stage.replace(/_/g, " ")} ·{" "}
                    {insights.biggest_drop.lost} users lost ({insights.biggest_drop.drop_pct}%)
                  </p>
                )}
                {insights.weakest && (
                  <p data-testid="af-weakest" className="text-[12.5px] text-slate-600 dark:text-slate-300 mt-1">
                    <span className="inline-flex items-center gap-1.5">
                      <Activity className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
                      <strong>Weakest funnel:</strong>
                    </span>{" "}
                    {insights.weakest.funnel.replace(/_/g, " ")} —{" "}
                    {insights.weakest.overall_pct}% of {insights.weakest.top_count} entries convert
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {loading && !data ? (
          <div className="text-center py-16 text-sm text-slate-500">Loading…</div>
        ) : !data?.funnels?.length ? (
          <div className="text-center py-16 text-sm text-slate-500">No funnel data yet — events appear once users hit the wedge products.</div>
        ) : (
          <div className="space-y-4">
            {data.funnels.map((f) => (
              <FunnelCard key={f.funnel} f={f} />
            ))}
          </div>
        )}

        {/* HOMEPAGE SECTIONS — per-section engagement breakdown */}
        {sections?.sections?.length > 0 && (
          <div data-testid="af-sections" className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-violet-100 dark:bg-violet-900/30 border border-violet-200 dark:border-violet-700 flex items-center justify-center flex-shrink-0">
                <Activity className="w-5 h-5 text-violet-600 dark:text-violet-400" />
              </div>
              <div className="flex-1">
                <p className="text-base font-bold text-slate-900 dark:text-white">Homepage sections</p>
                <p className="text-[11.5px] text-slate-500">Entered → dwelled ≥1.5s → CTA clicked. Highest-engagement at top.</p>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-[12.5px]">
                <thead>
                  <tr className="text-left text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-800">
                    <th className="py-2 font-semibold">Section</th>
                    <th className="py-2 font-semibold text-right">Entered</th>
                    <th className="py-2 font-semibold text-right">Attention %</th>
                    <th className="py-2 font-semibold text-right">Avg dwell</th>
                    <th className="py-2 font-semibold text-right">CTR %</th>
                  </tr>
                </thead>
                <tbody>
                  {sections.sections.map((s) => (
                    <tr key={s.section} data-testid={`af-section-${s.section}`} className="border-b border-slate-100 dark:border-slate-800">
                      <td className="py-2.5 font-semibold text-slate-700 dark:text-slate-200 capitalize">{s.section.replace(/_/g, " ")}</td>
                      <td className="py-2.5 text-right text-slate-600 dark:text-slate-300">{s.entered}</td>
                      <td className={`py-2.5 text-right font-bold ${
                        (s.attention_pct ?? 0) >= 60 ? "text-emerald-600 dark:text-emerald-400"
                        : (s.attention_pct ?? 0) >= 30 ? "text-amber-600 dark:text-amber-400"
                        : "text-rose-600 dark:text-rose-400"
                      }`}>{s.attention_pct ?? "—"}{s.attention_pct != null && "%"}</td>
                      <td className="py-2.5 text-right text-slate-600 dark:text-slate-300">{s.avg_dwell_seconds}s</td>
                      <td className={`py-2.5 text-right font-bold ${
                        (s.ctr_pct ?? 0) >= 5 ? "text-emerald-600 dark:text-emerald-400"
                        : (s.ctr_pct ?? 0) >= 1 ? "text-amber-600 dark:text-amber-400"
                        : "text-rose-600 dark:text-rose-400"
                      }`}>{s.ctr_pct ?? "—"}{s.ctr_pct != null && "%"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function FunnelCard({ f }) {
  const top = f.top_stage_count;
  const final = f.final_stage_count;
  const overall = f.overall_conversion_pct;

  return (
    <div data-testid={`af-funnel-${f.funnel}`} className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-5">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-amber-100 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 flex items-center justify-center flex-shrink-0">
          <Activity className="w-5 h-5 text-amber-600 dark:text-amber-400" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-base font-bold text-slate-900 dark:text-white">{f.label}</p>
          <p className="text-[11.5px] text-slate-500">{top} entries · {final} completions</p>
        </div>
        {overall !== null && (
          <div className="text-right">
            <div data-testid={`af-overall-${f.funnel}`} className={`text-xl font-extrabold ${overall >= 30 ? "text-emerald-600 dark:text-emerald-400" : overall >= 10 ? "text-amber-600 dark:text-amber-400" : "text-rose-600 dark:text-rose-400"}`}>
              {overall}%
            </div>
            <div className="text-[10px] text-slate-500 uppercase tracking-[0.18em]">overall</div>
          </div>
        )}
      </div>

      {/* Stage bars */}
      <div className="space-y-2.5">
        {f.stages.map((s, i) => {
          const pct = top ? Math.round((s.count / top) * 100) : 0;
          return (
            <div key={s.stage} data-testid={`af-stage-${f.funnel}-${s.stage}`} className="space-y-1">
              <div className="flex items-center justify-between text-[12px]">
                <div className="flex items-center gap-2 text-slate-700 dark:text-slate-200 font-semibold">
                  <span className="text-slate-400 font-mono">{i + 1}.</span>
                  <span>{s.stage.replace(/_/g, " ")}</span>
                  {s.from_prev_pct != null && (
                    <span className={`inline-flex items-center gap-0.5 text-[10px] font-bold rounded-full px-1.5 py-0.5 ${
                      s.from_prev_pct >= 50 ? "bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300"
                      : s.from_prev_pct >= 20 ? "bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300"
                      : "bg-rose-50 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300"
                    }`}>
                      {s.from_prev_pct >= 50 ? <ChevronRight className="w-2.5 h-2.5" /> : <TrendingDown className="w-2.5 h-2.5" />}
                      {s.from_prev_pct}%
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3 text-slate-500">
                  <span className="inline-flex items-center gap-1">
                    <Users className="w-3 h-3" /> {s.unique_users}
                  </span>
                  <span className="font-bold text-slate-700 dark:text-slate-200">{s.count}</span>
                </div>
              </div>
              <div className="h-1.5 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-amber-300 to-amber-500"
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
